import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  LinearProgress,
  Avatar,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Lock as LockedIcon,
  LockOpen as UnlockedIcon,
  Info as InfoIcon,
  Event as EventIcon,
  EmojiEvents as TournamentIcon,
  SportsEsports as GameIcon,
  Speed as TrackManiaIcon,
  GolfCourse as SkillIcon,
  Group as SocialIcon
} from '@mui/icons-material';
import AchievementModal from './AchievementModal';

// Rarity colors matching WoW theme
const RARITY_COLORS = {
  common: '#9d9d9d',
  rare: '#0070dd',
  epic: '#a335ee',
  legendary: '#ff8000'
};

// Category icons
const CATEGORY_ICONS = {
  events: EventIcon,
  tournaments: TournamentIcon,
  games: GameIcon,
  trackmania: TrackManiaIcon,
  skill: SkillIcon,
  social: SocialIcon
};

const AchievementCard = ({ achievement, showProgress = true }) => {
  const [modalOpen, setModalOpen] = useState(false);
  
  const {
    title,
    description,
    category,
    rarity,
    points,
    unlocked,
    progress = 0,
    current_value = 0,
    criteria_value,
    unlocked_at
  } = achievement;

  const rarityColor = RARITY_COLORS[rarity] || RARITY_COLORS.common;
  const CategoryIcon = CATEGORY_ICONS[category] || SocialIcon;
  
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <>
      <Card
        sx={{
          height: '100%',
          position: 'relative',
          opacity: unlocked ? 1 : 0.7,
          transition: 'all 0.3s ease',
          border: `2px solid ${unlocked ? rarityColor : '#e0e0e0'}`,
          '&:hover': {
            boxShadow: 6,
            opacity: 1
          },
          cursor: 'pointer'
        }}
        onClick={() => setModalOpen(true)}
      >
        <CardContent>
          {/* Header with icon and rarity */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Avatar
              sx={{
                bgcolor: rarityColor,
                width: 56,
                height: 56
              }}
            >
              <CategoryIcon sx={{ fontSize: 32 }} />
            </Avatar>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
              <Chip
                label={rarity.toUpperCase()}
                size="small"
                sx={{
                  bgcolor: rarityColor,
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '0.7rem'
                }}
              />
              <Chip
                label={`${points} pts`}
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.7rem' }}
              />
            </Box>
          </Box>

          {/* Title */}
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              fontWeight: 700,
              color: unlocked ? rarityColor : 'text.secondary',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            {unlocked ? <UnlockedIcon fontSize="small" /> : <LockedIcon fontSize="small" />}
            {title}
          </Typography>

          {/* Description */}
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 2,
              minHeight: '40px',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden'
            }}
          >
            {description}
          </Typography>

          {/* Progress bar (for locked achievements) */}
          {!unlocked && showProgress && criteria_value && (
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Progress
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {current_value} / {criteria_value}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: 'rgba(0,0,0,0.1)',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: rarityColor
                  }
                }}
              />
            </Box>
          )}

          {/* Unlock date */}
          {unlocked && unlocked_at && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Unlocked: {formatDate(unlocked_at)}
              </Typography>
            </Box>
          )}

          {/* Info icon */}
          <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
            <Tooltip title="View details">
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); setModalOpen(true); }}>
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </CardContent>
      </Card>

      {/* Achievement details modal */}
      <AchievementModal
        achievement={achievement}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </>
  );
};

export default AchievementCard;

